import { createSelector } from 'reselect';
import get from 'lodash/get';
import { data as nullableDataSelector } from '../FinancialPlanSelector';
import { isEmpty } from '../../../js/app/react/validators';

import {
  goalId as goalIdSelector,
  goalViewState as goalViewStateSelector,
  selectedGoal as selectedGoalSelector,
} from '../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { getGoal } from '../FinancialPlanHelpers';

const defined = obj => obj || {};
const DEFAULT_ACTION_STEP_TYPES = [
  {
    label: 'Suggestion',
    value: 10,
  },
  {
    label: 'AdditionalTips',
    value: 11,
  },
];

export const isAddingActionStepSelector = createSelector(
  goalViewStateSelector,
  goalViewState => !!defined(goalViewState).isAddingActionStep,
);
export const addActionStepSelector = createSelector(goalViewStateSelector, goalViewState =>
  defined(defined(goalViewState).addActionStep),
);
export const isAddingSuggestionSelector = createSelector(
  goalViewStateSelector,
  goalViewState => !!defined(goalViewState).isAddingSuggestion,
);
export const addSuggestionSelector = createSelector(goalViewStateSelector, goalViewState =>
  defined(defined(goalViewState).addSuggestion),
);

export const dataSelector = createSelector(nullableDataSelector, nullableData => {
  if (isEmpty(nullableData)) {
    return {};
  }
  return nullableData;
});

export const actionStepsTypesSelector = createSelector(dataSelector, data =>
  get(data, 'ActionStepTypeOption', DEFAULT_ACTION_STEP_TYPES),
);

export const allActionStepsSelector = createSelector(
  goalIdSelector,
  dataSelector,
  (_, props) => props.goalId,
  (goalId, data, propsGoalId) =>
    (data.PlanActionSteps || []).filter(({ GoalId }) => GoalId === (propsGoalId || goalId)),
);

export const isInsuranceGoalSelector = createSelector(
  dataSelector,
  goalIdSelector,
  (data, goalId) => {
    const goal = getGoal(data, goalId);
    return goal.GoalType === 'insurance';
  },
);

export const leftTitleSelector = createSelector(isInsuranceGoalSelector, () => 'Action Steps');

export const leftButtonTextSelector = createSelector(isInsuranceGoalSelector, () => 'Action Step');

export const rightTitleSelector = createSelector(isInsuranceGoalSelector, () => 'Suggestions');

export const rightButtonTextSelector = createSelector(isInsuranceGoalSelector, () => 'Suggestion');

export const suggestionTypeSelector = createSelector(
  actionStepsTypesSelector,
  actionStepsTypes => actionStepsTypes.find(({ label }) => label === 'Suggestion').value,
);

export const additionalTipsTypeSelector = createSelector(
  actionStepsTypesSelector,
  actionStepsTypes => actionStepsTypes.find(({ label }) => label === 'AdditionalTips').value,
);

export const suggestionsSelector = createSelector(
  allActionStepsSelector,
  suggestionTypeSelector,
  additionalTipsTypeSelector,
  selectedGoalSelector,
  (_, props) => props.goal,
  (allActionSteps, suggestionType, additionalTipsType, goal, propsGoal) => {
    const { isSubGoal, DebtType } = propsGoal || goal;

    if (isSubGoal) {
      return allActionSteps.filter(
        actionStep =>
          actionStep.DebtType === DebtType &&
          (actionStep.Type === suggestionType || actionStep.Type === additionalTipsType),
      );
    }

    return allActionSteps.filter(
      actionStep =>
        (actionStep.DebtType === undefined || actionStep.DebtType === null) &&
        (actionStep.Type === suggestionType || actionStep.Type === additionalTipsType),
    );
  },
);

export const actionStepsSelector = createSelector(
  allActionStepsSelector,
  suggestionTypeSelector,
  additionalTipsTypeSelector,
  selectedGoalSelector,
  (_, props) => props.goal,
  (allActionSteps, suggestionType, additionalTipsType, goal, propsGoal) => {
    const { isSubGoal, DebtType } = propsGoal || goal;

    if (isSubGoal) {
      return allActionSteps.filter(
        actionStep =>
          actionStep.Type !== suggestionType &&
          actionStep.Type !== additionalTipsType &&
          actionStep.IsTask === true &&
          actionStep.DebtType === DebtType,
      );
    }

    return allActionSteps.filter(
      actionStep =>
        actionStep.Type !== suggestionType &&
        actionStep.Type !== additionalTipsType &&
        actionStep.IsTask === true &&
        (actionStep.DebtType === undefined || actionStep.DebtType === null),
    );
  },
);

export const goalOnTrackSelector = createSelector(
  actionStepsSelector,
  actionSteps => !actionSteps.some(({ IsComplete }) => !IsComplete),
);

export const selectPlanActionStepsAdvisersAssignees = createSelector(
  dataSelector,
  ({ PlanActionStepsAdvisersAssignees }) => PlanActionStepsAdvisersAssignees || [],
);
